import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, Menu, X } from "lucide-react";
import { Link, useLocation } from "react-router-dom";

const LOGO_URL = "/communiti-logo.png";

const links = [
    { label: "Home", href: "/", type: "route" },
    { label: "Services", href: "/services", type: "route" },
    { label: "Blogs", href: "/blog", type: "route" },
    { label: "About", href: "/about", type: "route" },
    { label: "Contact", href: "/#contact", type: "hash" },
];
// { label: "Work", href: "/#work", type: "hash" }

export default function Navbar() {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const { pathname } = useLocation();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 24);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => {
        setOpen(false);
    }, [pathname]);

    const renderLink = (l, extraCls = "") =>
        l.type === "route" ? (
            <Link
                key={l.label}
                to={l.href}
                data-testid={`nav-link-${l.label.toLowerCase()}`}
                className={extraCls}
                onClick={() => setOpen(false)}
            >
                <span>{l.label}</span>
                <span className="absolute -bottom-1 left-0 h-px w-0 bg-communiti transition-all duration-300 group-hover:w-full" />
            </Link>
        ) : (
            <a
                key={l.label}
                data-testid={`nav-link-${l.label.toLowerCase()}`}
                href={l.href}
                className={extraCls}
                onClick={() => setOpen(false)}
            >
                <span>{l.label}</span>
                <span className="absolute -bottom-1 left-0 h-px w-0 bg-communiti transition-all duration-300 group-hover:w-full" />
            </a>
        );

    return (
        <motion.header
            data-testid="site-navbar"
            initial={{ y: -24, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
            className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
                scrolled
                    ? "bg-white/75 backdrop-blur-xl border-b border-communiti-line"
                    : "bg-transparent"
            }`}
        >
            <div className="mx-auto max-w-[1440px] px-4 sm:px-6 lg:px-10 h-[72px] sm:h-[80px] lg:h-[88px] flex items-center justify-between">
                <Link
                    data-testid="navbar-logo"
                    to="/"
                    className="flex items-center gap-2 shrink-0"
                >
                    <img
                        src={LOGO_URL}
                        alt="Commmmuniti Media Marketing"
                        className="h-12 lg:h-14 w-auto object-contain"
                    />
                </Link>

                <nav className="hidden lg:flex items-center gap-9">
                    {links.map((l) =>
                        renderLink(
                            l,
                            "text-[13px] font-medium tracking-wide text-communiti-text/80 hover:text-communiti relative group"
                        )
                    )}
                </nav>

                <div className="flex items-center gap-3">
                    <a
                        data-testid="nav-cta-lets-talk"
                                href="tel:+917987924110"
                        className="hidden md:inline-flex items-center gap-2 rounded-full bg-black text-white pl-5 pr-2 py-2 text-[13px] font-medium hover:bg-communiti transition-colors duration-300"
                    >
                        LET'S TALK
                        <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-white text-black">
                            <ArrowUpRight className="h-3.5 w-3.5" />
                        </span>
                    </a>
                    <button
                        data-testid="nav-mobile-toggle"
                        onClick={() => setOpen((v) => !v)}
                        aria-label="Toggle menu"
                        className="lg:hidden inline-flex items-center justify-center h-10 w-10 rounded-full border border-communiti-line bg-white"
                    >
                        {open ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
                    </button>
                </div>
            </div>

            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
                        className="lg:hidden overflow-hidden bg-white border-t border-communiti-line"
                    >
                        <div className="px-6 py-6 flex flex-col gap-4">
                            {links.map((l) =>
                                l.type === "route" ? (
                                    <Link
                                        key={l.label}
                                        to={l.href}
                                        data-testid={`mobile-nav-${l.label.toLowerCase()}`}
                                        onClick={() => setOpen(false)}
                                        className="text-2xl font-display font-bold text-communiti-text"
                                    >
                                        {l.label}
                                    </Link>
                                ) : (
                                    <a
                                        key={l.label}
                                        href={l.href}
                                        data-testid={`mobile-nav-${l.label.toLowerCase()}`}
                                        onClick={() => setOpen(false)}
                                        className="text-2xl font-display font-bold text-communiti-text"
                                    >
                                        {l.label}
                                    </a>
                                )
                            )}
                            <a
                        href="tel:+917987924110"
                                data-testid="mobile-cta"
                                onClick={() => setOpen(false)}
                                className="mt-2 inline-flex w-fit items-center gap-2 rounded-full bg-black text-white px-5 py-3 text-sm"
                            >
                                LET'S TALK <ArrowUpRight className="h-4 w-4" />
                            </a>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </motion.header>
    );
}
